import { externalLinks } from "@constants/links";
import { useCartInfo } from "@contexts/CartInfoContext";
import { usePurchaseFlow } from "@contexts/PurchaseFlowContext";
import { useSetupIntent } from "@hooks/useSetupIntent";
import {
  Box,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useGetStripeSetupIntentClientSecret } from "@queries/useGetStripeSetupIntentClientSecret";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import type { SupportedCurrency } from "@utils/currencyUtils";
import { useEffect, useMemo, useState } from "react";
import { Controller, type ControllerRenderProps, useFormContext } from "react-hook-form";
import LoadingOverlay from "../LoadingOverlay";
import ConfirmUnAuthenticated from "./ConfirmUnAuthenticated";

interface ConfirmPurchaseProps {
  hasAttemptedValidation?: boolean;
  title?: string;
}

export default function ConfirmUnAuthenticatedContainer({ title }: ConfirmPurchaseProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { setCurrentStep } = usePurchaseFlow();

  const [cardError, setCardError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>("Processing...");

  const { control, watch, setValue } = useFormContext();

  const uuid = watch("uuid");
  const clientSecret = watch("stripeClientSecret");
  const stripePaymentMethodId = watch("stripePaymentMethodId");

  // Get cart info from context - CartInfoContext handles all updatePurchase calls
  const { cartInfo, isLoading: isCartLoading, isTaxLoading, resetForConfirmPage } = useCartInfo();
  const selectedCurrency =
    (cartInfo?.currency as SupportedCurrency) ??
    (watch("selectedCurrency") as SupportedCurrency) ??
    "usd";
  const totalWithTax = cartInfo?.total_amount;

  const { refetch: getStripeSetupIntentClientSecret } = useGetStripeSetupIntentClientSecret(uuid);

  // Reset cart info on mount - clears stale server data so tax recalculates fresh
  useEffect(() => {
    resetForConfirmPage();
    return () => resetForConfirmPage();
  }, [resetForConfirmPage]);

  const summary = useMemo(
    () => [
      {
        title: "Contact Info:",
        value: `${watch("firstName")} ${watch("lastName")} (${watch("email")})`,
        isActive: !!watch("firstName") && !!watch("email"),
      },
      {
        title: "Organization Name:",
        value: watch("organizationName") || `${watch("firstName")} ${watch("lastName")}`,
        isActive: true,
      },
      {
        title: "Professional Credentials:",
        value: watch("professionalCredentials"),
        isActive: !!watch("professionalCredentials"),
      },
      {
        title: "Shipping Address:",
        value: (
          <>
            {watch("address1")}
            {watch("address2") && (
              <>
                <br />
                {watch("address2")}
              </>
            )}
            <br />
            {watch("city")}, {watch("state")}
            <br />
            {watch("postalCode")}, {watch("country")}
          </>
        ),
        isActive: !!watch("address1"),
      },
    ],
    [watch],
  );

  const getClientSecret = async (): Promise<string | null> => {
    const res = await getStripeSetupIntentClientSecret();
    return res.data?.data ?? null;
  };

  const { getPaymentMethodId, handleCreditCardChange } = useSetupIntent({
    stripe,
    elements,
    getClientSecret,
    setValue,
    setCardError,
    resetAgreementOnChange: true,
    getBillingDetails: () => {
      // Build billing details to store with Stripe PaymentMethod
      const firstName = watch("firstName") || "";
      const lastName = watch("lastName") || "";
      const email = watch("email") || undefined;
      const address1 = watch("address1") || undefined;
      const address2 = watch("address2") || undefined;
      const city = watch("city") || undefined;
      const state = watch("state") || undefined;
      const postalCode = watch("postalCode") || undefined;
      const country = watch("country") || undefined;

      return {
        name: `${firstName} ${lastName}`.trim(),
        email,
        address: {
          line1: address1,
          line2: address2,
          city,
          state,
          postal_code: postalCode,
          country,
        },
      };
    },
  });

  // Fetch client secret for card setup
  useEffect(() => {
    if (uuid && !clientSecret) {
      getStripeSetupIntentClientSecret().then((res) => {
        setValue("stripeClientSecret", res.data?.data);
      });
    }
  }, [uuid, clientSecret, getStripeSetupIntentClientSecret, setValue]);

  const handleAgreementChange = async (
    isChecked: boolean,
    field: ControllerRenderProps<Record<string, unknown>, "agreement">,
  ) => {
    field.onChange(isChecked);

    if (isChecked) {
      setLoadingMessage("verifying card...");
      setLoading(true);

      // Ensure we have a verified payment method
      const paymentMethodId = stripePaymentMethodId;
      if (!paymentMethodId) {
        const ok = await getPaymentMethodId();
        if (!ok) {
          setCardError("Card verification failed. Please try again.");
          field.onChange(false);
          setLoading(false);
          return;
        }
        // billingPostalCode is set by useSetupIntent when card is verified
        // CartInfoContext will automatically call updatePurchase
      }

      setCardError(null);
      setLoading(false);
    }
  };

  const agreementControl = (
    <Controller
      name="agreement"
      control={control}
      rules={{ required: true }}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Checkbox
              {...field}
              size="small"
              checked={Boolean(field.value)}
              disabled={loading || isCartLoading || isTaxLoading}
              onChange={(e) => handleAgreementChange(e.target.checked, field)}
            />
          }
          label={
            <Typography variant="caption">
              I accept the Unyte Health{" "}
              <a href={externalLinks.termsOfService} target="_blank" rel="noopener noreferrer">
                Customer Terms of Service
              </a>{" "}
              including the Professional Attestation.
            </Typography>
          }
        />
      )}
    />
  );

  const isLoadingOverlay = loading || isCartLoading || isTaxLoading;

  return (
    <Box>
      <CardHeader title={title} slotProps={{ title: { variant: "h5" } }} />
      <CardContent sx={{ pt: 0 }}>
        <ConfirmUnAuthenticated
          summary={summary}
          onEdit={() => setCurrentStep(1)}
          clientSecret={clientSecret}
          cardError={cardError}
          onCardChange={handleCreditCardChange}
          totalWithTax={totalWithTax}
          selectedCurrency={selectedCurrency}
          agreementControl={agreementControl}
        />
      </CardContent>
      <LoadingOverlay open={isLoadingOverlay} message={loadingMessage} />
    </Box>
  );
}
